/**
 * OTA (over-the-air) live updates for the bundled Capacitor mobile shell.
 *
 * The app always runs from a single bundled local origin
 * (`capacitor://localhost` / `https://localhost`) so it cold-starts offline and
 * keeps its IndexedDB/localStorage offline layer intact. This module layers OTA
 * on top WITHOUT leaving that origin: @capgo/capacitor-updater downloads a newer
 * `build/` web bundle to local storage and serves `webDir` from it — still under
 * the same `localhost` origin. The WebView is never navigated to a remote host.
 *
 * Manual mode (autoUpdate: false in capacitor.config.ts): this JS layer polls a
 * static manifest on our own CloudFront, semver-compares, enforces the
 * min-native-version floor, then download()/next()s the bundle to activate on the
 * NEXT launch (never a jarring mid-session reload). Automatic rollback is handled
 * natively by the plugin (appReadyTimeout + notifyAppReady()).
 *
 * See docs/platform/mobile-offline-cold-start.md (the design contract).
 */
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { App } from '@capacitor/app';
import { isCapacitor } from '../api/config';
import { getMainDomainOrigin } from '../utils/platform';

/**
 * OTA channel baked into the bundle at frontend build time. CI sets
 * VITE_OTA_CHANNEL per track: `production` for store users, `internal` mirroring
 * TestFlight / Play Internal. Defaults to `production`.
 */
const OTA_CHANNEL = (import.meta.env.VITE_OTA_CHANNEL as string | undefined) || 'production';

/** Shape of the static `ota/<channel>/manifest.json` published by CI. */
export interface OtaManifest {
  /** Build id of the latest published bundle (`<marketing>.<run_number>`). */
  version: string;
  /** Absolute CloudFront URL of the bundle zip. */
  url: string;
  /**
   * Plain SHA-256 (hex) of the bundle zip. The plugin recomputes the hash of
   * the downloaded zip and rejects it on mismatch (tamper detection). Bundles
   * are NOT encrypted — see capacitor.config.ts (no publicKey) — so this is a
   * plain hash, not an RSA-wrapped one.
   */
  checksum: string;
  /**
   * Oldest native binary version this bundle may apply to. Bumped by CI only on
   * native-affecting builds; a JS bundle needing newer native code will not
   * apply to an older binary (it waits for the store update).
   */
  minNativeVersion?: string;
  /** Channel this manifest belongs to (sanity check). */
  channel?: string;
}

/**
 * OTA is ONLY active on a production-bundled native app. Web never runs OTA
 * (offline there is data-only, no service worker). Local-dev native builds load
 * the LAN dev server over `http:` (run-mobile-local.sh) where the dev server is
 * the source of truth and HMR would fight the plugin — so those are excluded by
 * the protocol check, exactly like resolveApiBaseUrl() in api/config.ts.
 */
export function isOtaEnabled(): boolean {
  return isCapacitor && window.location.protocol !== 'http:';
}

/**
 * Compare two `<major>.<minor>.<patch>...` version strings numerically segment
 * by segment. Returns true iff `candidate` is strictly newer than `current`.
 * Non-numeric segments (e.g. the plugin's "builtin" sentinel) sort as oldest, so
 * any real published version is treated as newer than an unstamped builtin.
 */
export function isNewerVersion(candidate: string, current: string): boolean {
  const toParts = (v: string): number[] =>
    v.split(/[.+-]/).map((s) => {
      const n = Number.parseInt(s, 10);
      return Number.isNaN(n) ? -1 : n;
    });
  const a = toParts(candidate);
  const b = toParts(current);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    if (ai > bi) return true;
    if (ai < bi) return false;
  }
  return false;
}

/** Absolute URL of the channel manifest on the main CloudFront distribution. */
export function getManifestUrl(): string {
  // OTA bundles are hosted on the main app.panluma.ai CloudFront/S3 (where the
  // frontend build is deployed), NOT on per-tenant custom domains — so always
  // resolve against the main domain, like OAuth callbacks do.
  return `${getMainDomainOrigin()}/ota/${OTA_CHANNEL}/manifest.json`;
}

/**
 * Defence-in-depth: the manifest is fetched from the pinned main origin over
 * HTTPS, but the bundle `url` it carries is handed to the native downloader — so
 * verify it points back at the SAME origin (an attacker with S3 write access
 * must not be able to redirect downloads off-host) and that the channel matches
 * what this binary is pinned to (block same-bucket cross-channel substitution).
 */
function isTrustedManifest(manifest: OtaManifest): boolean {
  try {
    if (new URL(manifest.url).origin !== getMainDomainOrigin()) {
      console.warn(`[ota] rejecting bundle from unexpected origin: ${manifest.url}`);
      return false;
    }
  } catch {
    return false;
  }
  if (manifest.channel && manifest.channel !== OTA_CHANNEL) {
    console.warn(`[ota] rejecting cross-channel manifest: ${manifest.channel} != ${OTA_CHANNEL}`);
    return false;
  }
  return true;
}

// Guards against overlapping checks (rapid background/foreground cycling fires
// appStateChange repeatedly) — a second check is a no-op while one is in flight.
let checkInFlight = false;

/**
 * Tell the plugin the currently-running bundle booted successfully, so it is
 * marked known-good and not rolled back. Safe to call on every launch.
 */
export async function notifyReady(): Promise<void> {
  if (!isOtaEnabled()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (err) {
    // Non-fatal: on the baked-in bundle there may be nothing pending. Never let
    // OTA bookkeeping crash app startup.
    console.warn('[ota] notifyAppReady failed', err);
  }
}

/**
 * Check the channel manifest and, if a newer compatible bundle exists, download
 * it and stage it to activate on the next launch. Best-effort — any failure
 * (offline, bad manifest, download error) is swallowed; the app keeps running
 * its current bundle.
 */
export async function checkForUpdate(): Promise<void> {
  if (!isOtaEnabled() || checkInFlight) return;
  checkInFlight = true;
  try {
    const res = await fetch(getManifestUrl(), { cache: 'no-store' });
    if (!res.ok) return;
    const manifest = (await res.json()) as OtaManifest;
    if (!manifest?.version || !manifest?.url || !manifest?.checksum) return;
    if (!isTrustedManifest(manifest)) return;

    const { bundle, native } = await CapacitorUpdater.current();

    // Already on this build (or newer) — nothing to do. Covers the
    // fresh-install case where the baked-in bundle is stamped with the same
    // build id the matching OTA manifest carries (see capacitor.config.ts).
    if (!isNewerVersion(manifest.version, bundle.version)) return;

    // Native-floor guard: a web bundle that needs a newer native binary must
    // wait for the store build. `native` is the installed binary's version.
    if (manifest.minNativeVersion && isNewerVersion(manifest.minNativeVersion, native)) {
      console.info(
        `[ota] bundle ${manifest.version} requires native >= ${manifest.minNativeVersion} ` +
          `but binary is ${native}; waiting for store update`,
      );
      return;
    }

    // Plain checksum, no sessionKey: the plugin verifies the SHA-256 against the
    // downloaded zip and rejects a mismatch. No decryption step (bundles aren't
    // encrypted — capacitor.config.ts bakes no publicKey).
    const downloaded = await CapacitorUpdater.download({
      url: manifest.url,
      version: manifest.version,
      checksum: manifest.checksum,
    });
    // next() — NOT set(). next() queues the bundle to activate on the next app
    // background/launch with no mid-session reload; set() would reload the WebView
    // immediately, interrupting whatever the user is doing. Apply-on-next-launch is
    // the design contract (mobile-offline-cold-start.md): the offline layer and the
    // user's current screen stay put until a natural relaunch.
    await CapacitorUpdater.next({ id: downloaded.id });
    console.info(`[ota] staged bundle ${manifest.version}; applies on next launch`);
  } catch (err) {
    console.warn('[ota] update check failed', err);
  } finally {
    checkInFlight = false;
  }
}

/**
 * Wire up the OTA lifecycle: confirm the current bundle is good, check once on
 * launch, and re-check whenever the app returns to the foreground (the "a launch
 * or two after coming online" cadence the design doc accepts). No-op off-native.
 */
export async function initLiveUpdates(): Promise<void> {
  if (!isOtaEnabled()) return;
  await notifyReady();
  void checkForUpdate();
  await App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) void checkForUpdate();
  });
}
